import { marked } from 'marked'
import { truncateAtWordBoundary } from './search-constants'

/**
 * Texte brut d'une description Markdown, pour les sorties qui ne rendent pas
 * de HTML : meta `description`, `og:description`, `twitter:description`,
 * JSON-LD, texte de partage, kit Promouvoir (4.130.8).
 *
 * Les descriptions de gîte et de chambres d'hôtes sont saisies dans
 * `HoliRichTextarea` et stockées en Markdown. Reprises telles quelles, elles
 * sortaient avec leur syntaxe : la meta description de la CH `le-petit-moulin`
 * commençait par `### `.
 *
 * Passer par `marked` plutôt que par des regex : il applique les règles de
 * l'emphase (intra-mot, espaces), si bien que `nuits_min` ou `50 € * 2`
 * restent intacts, là où l'ancien `stripMarkdown` de Promouvoir écrivait
 * `nuitsmin`. Seul le `++souligné++` de TipTap, que `renderMarkdown`
 * (`app/composables/useMarkdownRenderer.ts`) traite à part, est pré-traité ici.
 * `normalizeMarkers` n'est pas rejoué : `HoliRichTextarea` l'applique déjà à
 * l'écriture. Ce module n'importe pas le composable : il tourne aussi côté
 * serveur, sans DOMPurify.
 *
 * Les options sont passées à l'appel, jamais via `marked.setOptions` :
 * l'instance `marked` est partagée avec le rendu du blog public.
 *
 * Une entrée vide ou blanche donne `''` : chaque page garde son propre texte
 * de repli. Les troncatures se font APRÈS la conversion, chez l'appelant.
 */
export function markdownToPlainText(md: string | null | undefined): string {
  if (!md || !md.trim()) return ''

  // Voie rapide : la recherche convertit chaque ligne de résultat, en FR et en
  // EN. Un texte sans aucun caractère que marked pourrait changer en balise
  // ressort à l'identique, espaces fusionnés : inutile de l'analyser.
  if (!MARKDOWN_SYNTAX.test(md)) return collapseWhitespace(md)

  return plainTextViaMarked(md)
}

/**
 * Tout ce qui peut produire une balise ou une entité : caractères d'emphase,
 * de titre, de citation, de lien, de code, d'entité, de HTML, d'échappement et
 * de table ; ligne commençant par une puce, un soulignement de titre ou un
 * numéro de liste ; bloc de code indenté. Au moindre doute, marked tranche.
 */
const MARKDOWN_SYNTAX = /[*_~+#>[\]()`&<\\|]|^[ \t]*[-=]|^[ \t]*\d+[.)]|^(?: {4}|\t)/m

function collapseWhitespace(text: string): string {
  return text.replace(/\s+/g, ' ').trim()
}

/**
 * Conversion complète par marked. Exportée pour les tests seulement : ils
 * vérifient que la voie rapide donne le même texte.
 */
export function plainTextViaMarked(md: string): string {
  // `++souligné++` (syntaxe TipTap, inconnue de marked) : le contenu seul.
  // Mêmes bornes que `normalizeMarkers` : ni lettre, ni chiffre, ni `+` avant
  // l'ouvrant ou après le fermant, un caractère non blanc contre chacun, rien
  // à travers un retour à la ligne. `A++.`, `C++11` et `C++ et` restent intacts.
  const prepared = md.replace(/(?<![\p{L}\p{N}+])\+\+(?=\S)([^\n]+?)(?<=\S)\+\+(?![\p{L}\p{N}+])/gu, '$1')

  const html = marked.parse(prepared, { async: false, gfm: true, breaks: false }) as string

  return decodeEntities(
    html
      // Une balise de bloc ou un saut de ligne sépare deux mots : espace.
      .replace(/<\/?(?:p|div|h[1-6]|ul|ol|li|blockquote|pre|table|thead|tbody|tfoot|tr|td|th)\b[^>]*>|<br\s*\/?>|<hr\s*\/?>/gi, ' ')
      // Une balise en ligne (`<strong>`, `<em>`, `<a>`…) colle à son texte.
      .replace(/<[^>]*>/g, ''),
  )
    .replace(/\s+/g, ' ')
    .trim()
}

const NAMED_ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: '\'',
  nbsp: ' ',
}

/**
 * Décodage en UNE passe : `&amp;lt;` donne `&lt;`, jamais `<`. `marked`
 * ré-échappe `&`, `<`, `>` et `"`, et laisse en l'état les entités déjà
 * présentes dans la source (TipTap écrit `&amp;` après une frappe).
 */
function decodeEntities(text: string): string {
  return text.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (match, code: string) => {
    if (code[0] === '#') {
      const point = code[1] === 'x' || code[1] === 'X'
        ? Number.parseInt(code.slice(2), 16)
        : Number.parseInt(code.slice(1), 10)
      return Number.isFinite(point) && point > 0 && point <= 0x10FFFF
        ? String.fromCodePoint(point)
        : match
    }
    return NAMED_ENTITIES[code.toLowerCase()] ?? match
  })
}

/**
 * Extrait d'une description pour une carte de recherche (gîte ou chambres
 * d'hôtes) : texte brut, tronqué APRÈS conversion à la frontière de mot.
 * `null` si la description est absente ou ne contient aucun texte.
 */
export function toPlainTextExcerpt(md: string | null | undefined, maxLength: number): string | null {
  const plain = markdownToPlainText(md)
  return plain ? truncateAtWordBoundary(plain, maxLength) : null
}
